import { commandBus } from '@/features/chat'
import { useStudioStore } from '@/store/studio-store'
import { useStudioLocale } from '@/i18n'
import { getDataSurfaceCopy } from '../../data-surface-copy'
import type { DataSourceEntity } from '../../hooks/use-entity-index'
import { useDatasourceUsageIndex } from '../../hooks/use-usage-index'

function selectDatasourceEntity(d: DataSourceEntity) {
  commandBus.dispatch({
    type: 'selectEntity',
    payload: { blockName: d.blockName, category: 'datasources', name: d.name },
  })
  useStudioStore.getState().selectEntity(d.id)
}

export function DatasourceTable({ datasources }: { datasources: DataSourceEntity[] }) {
  const { locale, strings } = useStudioLocale()
  const copy = getDataSurfaceCopy(locale)
  const fields = strings.dataInspector.fields
  const dsUsageIndex = useDatasourceUsageIndex()

  if (datasources.length === 0) {
    return <p className="text-xs text-muted-foreground p-4">{copy.emptyTitle.datasources}</p>
  }

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-border text-left text-muted-foreground">
          <th className="px-3 py-2 font-medium">{fields.block}</th>
          <th className="px-3 py-2 font-medium">{fields.name}</th>
          <th className="px-3 py-2 font-medium">{fields.type}</th>
          <th className="px-3 py-2 font-medium">{fields.endpoint} / {fields.path}</th>
          <th className="px-3 py-2 font-medium">{fields.usedBy}</th>
        </tr>
      </thead>
      <tbody>
        {datasources.map((d) => {
          const usages = dsUsageIndex.get(d.name) ?? []
          const usageNames = usages
            .map((u) => u.flowName ?? u.blockName)
            .filter(Boolean)
            .join(', ')

          return (
            <tr
              key={d.id}
              data-testid="datasource-row"
              data-datasource-name={d.name}
              data-datasource-block={d.blockName}
              className="border-b border-border/50 hover:bg-accent/50 cursor-pointer transition-colors"
              onClick={() => selectDatasourceEntity(d)}
            >
              <td className="px-3 py-1.5 text-muted-foreground">
                {d.scope === 'app' ? strings.dataInspector.appManifest.scopeValue : d.blockName}
              </td>
              <td className="px-3 py-1.5 font-mono text-primary">{d.name}</td>
              <td className="px-3 py-1.5">
                <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{d.sourceType}</span>
              </td>
              <td className="px-3 py-1.5 font-mono text-muted-foreground truncate max-w-[200px]">
                {String(d.config.endpoint ?? d.config.path ?? d.config.url ?? '—')}
              </td>
              <td className="px-3 py-1.5">
                {usages.length > 0 ? (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                    title={usageNames}
                  >
                    {copy.statusFlows(usages.length)}
                  </span>
                ) : (
                  <span className="text-muted-foreground/50 text-[10px]">{copy.unused}</span>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
